import { useState } from "react";
import { getOrderStatus } from "@/functions/getOrderStatus";
import { useLanguage } from "@/components/LanguageContext";
import { motion } from "framer-motion";
import { Search, CheckCircle, Truck, Clock, XCircle, PackageCheck, Loader2, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const STEPS = [
  { key: "pending", labelKey: "track_placed", descKey: "track_placed_desc", icon: Clock },
  { key: "confirmed", labelKey: "track_confirmed", descKey: "track_confirmed_desc", icon: CheckCircle },
  { key: "shipped", labelKey: "track_shipped", descKey: "track_shipped_desc", icon: Truck },
  { key: "delivered", labelKey: "track_delivered", descKey: "track_delivered_desc", icon: PackageCheck },
];

const STATUS_INDEX = { pending: 0, confirmed: 1, shipped: 2, delivered: 3 };

function Info({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] md:text-xs font-bold text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-sm md:text-base font-semibold text-gray-900 break-words ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
}

export default function TrackOrderPage() {
  const { t, language } = useLanguage();
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);

  const handleTrack = async (e) => {
    e.preventDefault();
    const id = orderId.trim();
    const em = email.trim();
    if (!id || !em) {
      setError(language === "ar" ? "الرجاء إدخال رقم الطلب والبريد الإلكتروني." : "Please enter your Order ID and the email used at checkout.");
      return;
    }
    setLoading(true);
    setError(null);
    setOrder(null);
    try {
      const res = await getOrderStatus({ orderId: id, email: em });
      setOrder(res.data);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || t("track_err");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const isCancelled = order?.order_status === "cancelled";
  const currentIndex = order ? (STATUS_INDEX[order.order_status] ?? -1) : -1;
  const created = order?.created_date
    ? new Date(order.created_date).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", { year: "numeric", month: "short", day: "numeric" })
    : null;

  return (
    <div className="bg-white min-h-screen">
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-5xl font-black mb-3">
            {t("track_hero")}
          </motion.h1>
          <p className="text-sm md:text-lg text-gray-300">{t("track_sub")}</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 -mt-8 md:-mt-10">
        <Card className="border-0 shadow-2xl">
          <CardContent className="p-5 md:p-8">
            <form onSubmit={handleTrack} className="flex flex-col sm:flex-row gap-3">
              <Input
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder={t("track_search_ph")}
                className="h-12 text-base flex-1"
              />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={language === "ar" ? "البريد الإلكتروني المستخدم عند الشراء" : "Email used at checkout"}
                className="h-12 text-base flex-1"
              />
              <Button type="submit" disabled={loading} className="h-12 bg-red-600 hover:bg-red-700 px-6 font-bold whitespace-nowrap">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="flex items-center gap-1"><Search className="w-5 h-5" />{t("track_btn")}</span>}
              </Button>
            </form>

            {error && (
              <div className="mt-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {order && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <Info label={t("track_order_id_label")} value={order.id} mono />
                  <Info label={t("track_order_date")} value={created || "—"} />
                  <Info label={t("track_qty")} value={order.quantity || 1} />
                  <Info label={t("track_total")} value={order.total_amount != null ? `$${Number(order.total_amount).toFixed(2)}` : "—"} />
                  <Info label={t("track_payment")} value={order.payment_status === "paid" ? t("track_paid") : t("track_unpaid")} />
                  <Info label={t("track_dest")} value={[order.city, order.country].filter(Boolean).join(", ") || "—"} />
                </div>

                {isCancelled ? (
                  <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-5">
                    <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-black text-red-700">{t("track_cancelled")}</div>
                      <div className="text-sm text-red-600">{t("track_cancelled_desc")}</div>
                    </div>
                  </div>
                ) : (
                  <ol className="relative">
                    {STEPS.map((step, i) => {
                      const done = i < currentIndex;
                      const active = i === currentIndex;
                      const upcoming = i > currentIndex;
                      const Icon = step.icon;
                      return (
                        <li key={step.key} className="relative flex gap-4 pb-6 last:pb-0">
                          {i < STEPS.length - 1 && (
                            <span className={`absolute start-5 top-11 w-0.5 h-[calc(100%-1.5rem)] ${done ? "bg-red-600" : "bg-gray-200"}`} />
                          )}
                          <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${
                            active ? "bg-red-600 border-red-600 text-white" : done ? "bg-red-100 border-red-600 text-red-600" : "bg-white border-gray-300 text-gray-400"
                          }`}>
                            {done ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                          </div>
                          <div className="pt-1">
                            <div className={`font-bold text-sm md:text-base ${upcoming ? "text-gray-400" : "text-gray-900"}`}>
                              {t(step.labelKey)}
                            </div>
                            {!upcoming && <div className="text-xs md:text-sm text-gray-500">{t(step.descKey)}</div>}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </motion.div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}